/**
 * @wtc/meridian - Reusable Meridian AI Agent System
 * 
 * This package exports both CLIENT-SIDE and SERVER-SIDE code.
 * 
 * CLIENT-SIDE EXPORTS (use in React components):
 * - Components: ChatPage, ChatSidebar, ChatInterface, ChatActions, etc.
 * - Contexts: ChatSelectionProvider, ChatDataProvider
 * - Hooks: useChat, useChatList, useMessageStream, useBusinessInfo
 * - Types: Chat, ChatMessage, AIMessage, etc.
 * 
 * SERVER-SIDE EXPORTS (use in API routes only):
 * - API Handlers: handleCreateChat, handleListChats, handleGetChat, handleSendMessage, loadChat, etc.
 * - Tool System: toolRegistry, toolDefinitions, executeTool, getAvailableTools
 * - Tool Loaders: loadHandler, loadHandlers, attachHandlersToTools
 * - Tool Schemas: Pre-built Zod schemas for all tools
 * - AI Integration: convertToolsForAI, getAvailableToolsForAI
 * - Utilities: RAGEngine, Bedrock client, Auth utilities
 * 
 * IMPORTANT: Do not import server-side exports in client components.
 * Do not import client components in API routes.
 */

// Components (CLIENT-SIDE)
export { ChatPage } from './components/ChatPage';
export { ChatSidebar } from './components/ChatSidebar';
export { ChatInterface } from './components/ChatInterface';
export { ChatActions } from './components/ChatActions';
export { ShareChatModal } from './components/ShareChatModal';
export { ChatTokenUsageModal } from './components/ChatTokenUsageModal';
export { ChatSkillsModal } from './components/ChatSkillsModal';
export { CHAT_SKILLS_MODAL_ID } from './components/skills/constants';
export type { AttachedSkill, MeridianSkillPickerItem } from './components/skills/types';
export { BusinessInfoSettings } from './components/BusinessInfoSettings';
export { SlackAvailabilityCallout } from './components/SlackAvailabilityCallout';
export type { SlackAvailabilityCalloutProps } from './components/SlackAvailabilityCallout';
export { ArtifactCard, ArtifactViewer } from './components/artifacts';
export type { ArtifactCardProps, ArtifactViewerProps } from './components/artifacts';

// Contexts
export { ChatSelectionProvider, useChatSelection } from './contexts/ChatIdContext';
export type { ChatSelectionContextType } from './contexts/ChatIdContext';

// Backwards compatibility aliases
export { ChatIdProvider, useChatId } from './contexts/ChatIdContext';
export type { ChatIdContextType } from './contexts/ChatIdContext';

// Chat data context for centralized state management
export { ChatDataProvider, useChatData } from './contexts/ChatDataContext';
export type { ChatDataContextType } from './contexts/ChatDataContext';

// Chat activity — "which of my chats are running right now" (tab + sidebar dots)
export { ChatActivityProvider, useChatActivityOptional } from './contexts/ChatActivityContext';
export type { ChatActivityContextValue, ChatRunStatus } from './contexts/ChatActivityContext';

// Chat tabs (opt-in, per-user)
export { useChatTabs, MAX_CHAT_TABS } from './hooks/useChatTabs';
export type { ChatTab, UseChatTabsResult } from './hooks/useChatTabs';
export { ChatTabStrip } from './components/ChatTabStrip';
export type { ChatTabStripProps } from './components/ChatTabStrip';

// Hooks
export { useChat } from './hooks/useChat';
export { useChatList } from './hooks/useChatList';
export { useMessageStream } from './hooks/useMessageStream';
export { useBusinessInfo } from './hooks/useBusinessInfo';

// API Handlers (SERVER-SIDE)
export {
  handleCreateChat,
  handleListChats,
  handleGetChat,
  handleUpdateChat,
  handleDeleteChat,
} from './api/handlers/chat-handlers';
export { handleShareChat } from './api/handlers/share-handlers';
export { handleArchiveChat, handleUnarchiveChat } from './api/handlers/archive-handlers';
export { handleSendMessage } from './api/handlers/message-handlers';
export { loadChat, canonicalizeUIMessagePartsForPersistence } from './lib/db/chat-persistence';
export { handleUploadBusinessInfo, handleGetBusinessInfo } from './api/handlers/business-info-handlers';

// Types
export type {
  ChatId,
  MessageId,
  UserId,
  CognitoSub,
  Timestamp,
  MessageRole,
  ChatStatus,
  ChatMessage,
  Chat,
  ChatSummary,
  SharedUser,
  ShareChatRequest,
  ShareChatResponse,
  BusinessInfo,
  BusinessInfoWithMetadata,
  RAGDocument,
  RAGContext,
  CreateChatRequest,
  CreateChatResponse,
  ListChatsQuery,
  ListChatsResponse,
  GetChatResponse,
  UpdateChatRequest,
  UpdateChatResponse,
  SendMessageRequest,
  UploadBusinessInfoRequest,
  UploadBusinessInfoResponse,
  GetBusinessInfoResponse,
  ApiErrorResponse,
  ApiSuccessResponse,
} from './types';

// File types (from db/types)
export type { FilePart, FileUploadResponse } from './lib/db/types';

export {
  toChatId,
  toMessageId,
  toUserId,
  toCognitoSub,
  toTimestamp,
  isErrorResponse,
  isSuccessResponse,
  isChatMessage,
  isChat,
} from './types';

// Config
export type {
  AskAlanV2Config,
  ResolveImageUrl,
  ResolveFileExtract,
  ResolveActiveSkillPlaybook,
} from './config/config-types';
export { DEFAULT_CONFIG } from './config/default-config';

// Utilities
export { generateChatId, generateMessageId, getCurrentTimestamp, generateChatTitle } from './lib/utils/chat-utils';
// Chat access policy. ONE predicate (`chatAccessFor`) behind the send path, the
// chat mutation handlers and the read path, so a chat cannot be readable
// through one and refused by another.
export {
  chatAccessFor,
  validateChatAccess,
  isChatOwner,
  isChatSharedWithUser,
  isSlackMentionChat,
} from './lib/utils/chat-utils';
export type { ChatAccessFields, ChatAccessKind } from './lib/utils/chat-utils';
export { resolveChatForRead } from './lib/access/chat-read-access';
export type { ChatReadAccess } from './lib/access/chat-read-access';
export { convertToAIMessages, createChatMessage, filterMessagesByRole, getLastNMessages } from './lib/utils/message-utils';
export { validateRequestBody } from './lib/utils/validation';

// File Context Utilities (SERVER-SIDE)
export {
  extractFilePartsFromMessages,
  buildFileContextString,
  extractFileContext,
  enhanceSystemPromptWithFileContext,
  getFileContextMetadata,
  validateFileParts,
  transformFilePartsForAI,
} from './lib/file-context';
export {
  authenticatedFetch,
  authenticatedFetchResponse,
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
  authenticatedPatch,
  authenticatedDelete,
  getAuthToken,
  setAuthTokenGetter,
  setExtraRequestHeadersProvider,
  getExtraRequestHeaders,
} from './lib/api/authenticated-fetch';
export type { AuthenticatedFetchOptions, AuthenticatedFetchError } from './lib/api/authenticated-fetch';

// RAG (SERVER-SIDE)
export { RAGEngine } from './lib/rag/rag-engine';
export { ZillisKnowledgeBaseClient } from './lib/rag/zillis-client';
export type { KnowledgeBaseClient } from './lib/rag/knowledge-base-client';

// Meridian model provider — Claude Platform on AWS (SERVER-SIDE)
export { createMeridianProvider, streamBedrockResponse, generateBedrockResponse } from './lib/bedrock/bedrock-client';

// Auth
// Note: requireSupabaseAuth is server-only and should not be exported
// from the public API. Import directly from './lib/auth/supabase-auth' in API handlers.

// Prompt Builder
export { buildSystemPrompt, formatMessagesForAI } from './lib/prompts/prompt-builder';
export {
  formatEntityDataForPrompt,
  resolveEntityContextCaps,
  DEFAULT_ENTITY_FIELD_MAX_CHARS,
  DEFAULT_ENTITY_CONTEXT_MAX_CHARS,
  ENTITY_FIELD_TRUNCATION_MARKER,
  type EntityContextCaps,
  type EntityDataFormatResult,
} from './lib/prompts/entity-context-budget';

// Tool System - Registry and Definitions (SERVER-SIDE)
// Note: toolDefinitions is now sourced from @/lib/chat/tools/tool-definitions in hub
export { toolRegistry, initializeToolRegistry } from './lib/tools/registry';

// Tool System - Execution (SERVER-SIDE)
export { executeTool, getAvailableTools, getToolSchemas, toolExists } from './lib/tools/execution';
export { validateToolParameters, validateToolParametersOrThrow } from './lib/tools/execution';

// Tool System - Handlers (SERVER-SIDE)
export { FindContactHandler } from './lib/tools/handlers/query/find-contact.handler';
export { FindCompanyHandler } from './lib/tools/handlers/query/find-company.handler';
export { SearchCRMHandler } from './lib/tools/handlers/query/search-crm.handler';
export { AuditContactTimelineHandler } from './lib/tools/handlers/query/audit-contact-timeline.handler';
export { ListContactsHandler } from './lib/tools/handlers/query/list-contacts.handler';
export { EditContactHandler } from './lib/tools/handlers/edit/edit-contact.handler';
export { EditCompanyHandler } from './lib/tools/handlers/edit/edit-company.handler';
export { CreateContactActivityHandler } from './lib/tools/handlers/edit/create-contact-activity.handler';

// Tool System - Types (SERVER-SIDE)
export type { Tool, ToolResult, ToolExecutionContext } from './lib/tools/types';

// Tool System - Schemas (SERVER-SIDE - Pre-built Zod schemas)
export {
  findContactSchema,
  findCompanySchema,
  searchCrmSchema,
  auditContactTimelineSchema,
  listContactsSchema,
  editContactSchema,
  editCompanySchema,
  createContactActivitySchema,
  toolZodSchemas,
  getToolSchema,
} from './lib/tools/schemas';

// Tool System - Loaders (SERVER-SIDE - Lazy-load handlers)
export {
  loadHandler,
  loadHandlers,
  loadAllHandlers,
  attachHandlersToTools,
  clearHandlerCache,
  getHandlerCacheSize,
} from './lib/tools/loaders';

// Tool System - AI Integration (SERVER-SIDE - AI SDK integration)
export {
  convertToolsForAI,
  getAvailableToolsForAI,
  validateToolParametersWithSchema,
} from './lib/tools/ai-integration';

// Tool Search Metrics (SERVER-SIDE - admin/metrics)
export { getAggregatedMetrics } from './lib/tools/tool-search/metrics';

// Artifacts — data-artifact part schema + create_artifact tool (schema/types are
// isomorphic; buildCreateArtifactTool/executeCreateArtifact are SERVER-SIDE)
export {
  ARTIFACT_PART_TYPE,
  ARTIFACT_DATA_NAME,
  ARTIFACT_KINDS,
  artifactDataSchema,
  MERIDIAN_DATA_PART_SCHEMAS,
  MERIDIAN_ARTIFACT_MAX_CHARS_DEFAULT,
  resolveArtifactMaxChars,
  isArtifactUIPart,
  createArtifactEmitterRef,
  CREATE_ARTIFACT_TOOL_ID,
  createArtifactInputSchema,
  executeCreateArtifact,
  buildCreateArtifactTool,
  createArtifactToolDefinition,
  isCreateArtifactFailure,
} from './lib/artifacts';
export type {
  ArtifactData,
  ArtifactKind,
  ArtifactUIPart,
  EmitArtifact,
  ArtifactEmitterRef,
  CreateArtifactInput,
  CreateArtifactAck,
  CreateArtifactFailure,
  CreateArtifactResult,
} from './lib/artifacts';

